import type { ProfilePieces } from '@/modules/profile/functions/profilePieces';
import type { ProfileSeed } from '@/modules/profile/utils/profileSeedFromSearch';
import type { RevealState, SectionId } from '@/modules/profile/utils/sectionReadiness';

/**
 * The page is a client-fetch shell (rendered progressively in the browser) whenever SSR did not
 * deliver the profile. Full SSR renders and SSR error pages stay as today.
 */
export function isProgressiveProfileShell(p: {
  shouldFetchOnClient: boolean;
  hasInformation: boolean;
  isSsrErrorPage: boolean;
}): boolean {
  return !p.isSsrErrorPage && (p.shouldFetchOnClient || !p.hasInformation);
}

/**
 * Per-position in-order reveal (same rule as `revealInOrder`, but ids may repeat —
 * e.g. several Hamdast 'widgets' blocks — so the result is indexed by position, not id).
 *
 * `revealed` (one set per doctor, kept by the caller) makes a reveal final: a section that was
 * shown never goes back to its skeleton. Readiness can flip back — the booking card's chat-lifecycle
 * gate restarts when its query key moves from the slug to the doctor's user id — and without this
 * the card vanished and reappeared ~300ms later, remounting Services (a second
 * `online_visit_visibility` event). Keyed `id#n` (n-th occurrence of the id), not by position,
 * since the number of widget blocks can change.
 */
export function revealSectionStates(
  order: SectionId[],
  ready: (id: SectionId) => boolean,
  revealed?: Set<string>,
): RevealState[] {
  let open = true;
  const seen: Partial<Record<SectionId, number>> = {};
  return order.map(id => {
    const n = (seen[id] = (seen[id] ?? -1) + 1);
    const key = `${id}#${n}`;
    open = open && (!!revealed?.has(key) || ready(id));
    if (open) revealed?.add(key);
    return open ? 'decided' : 'pending';
  });
}

type HeadArgs = { displayName?: any; imageUrl?: any; serviceList?: any; pageViewCount?: any; [k: string]: any };

/** What the header knows on the progressive path; `null` on SSR renders (args pass through untouched). */
export type HeadProgress = { seed: ProfileSeed | null; pieces: ProfilePieces };

/**
 * Header args while pieces are pending: search-seed fallbacks for name/chips, and
 * `pageViewPending` (ProfileHead renders a placeholder instead of formatting a missing count, which
 * printed "NaN"). The avatar URL is chosen separately (`pickHeadImage`), it needs load state.
 */
export function progressiveHeadArgs<T extends HeadArgs>(args: T, progress: HeadProgress | null): T & { pageViewPending?: boolean } {
  if (!progress) return args;
  const { seed, pieces } = progress;
  const pageViewPending = args.pageViewCount == null && pieces.pageView === undefined;
  if (!seed) return { ...args, pageViewPending };
  const seedServices = String(seed.expertiseText ?? '')
    .split('|')
    .map(s => s.trim())
    .filter(Boolean);
  return {
    ...args,
    // Until the name piece arrives the assembled display_name is a slug-derived fallback
    // («دکتر-…-1» → «دکتر … 1»); the seed is the real name, so it must win — else the header
    // shows the wrong text and re-wraps when the name lands (CLS).
    displayName: pieces.fullName === undefined ? seed.display_name : args.displayName ?? seed.display_name,
    serviceList: pieces.expertise === undefined && seedServices.length > 0 ? seedServices : args.serviceList,
    // Experience comes from the expertise piece (graduation date); until then use the search value.
    title: pieces.expertise === undefined && seed.experience ? `${seed.experience} سال تجربه` : (args as any).title,
    pageViewPending,
  };
}

const picId = (url?: string | null) => (url ? /pic\.paziresh24\.com\/api\/image\/(\d+)/.exec(url)?.[1] ?? null : null);

/**
 * Header avatar on the progressive path. `undefined` = still unknown → avatar placeholder.
 * - A search seed wins from the first frame (same URL the card used → browser cache hit). It stays
 *   while the profile URL is unknown or shows the same picture (the thumb variant of the same id:
 *   swapping would only re-download a smaller copy). A *different* picture replaces it only once that
 *   picture has loaded (`finalLoaded`), so there is never an empty circle in between.
 * - Without a seed: the placeholder until the profile URL is final, never the "noimage" fallback
 *   that the assembly returns while the doctor's user id is still pending.
 */
export function pickHeadImage(p: {
  seedImage?: string;
  finalUrl?: string;
  /** the profile URL will not change any more (a real picture, or every piece settled) */
  finalKnown: boolean;
  finalLoaded: boolean;
}): string | undefined {
  const { seedImage, finalUrl, finalKnown, finalLoaded } = p;
  if (!finalKnown) return seedImage;
  if (!seedImage || !finalUrl) return finalUrl || seedImage;
  if (finalUrl === seedImage) return seedImage;
  const seedId = picId(seedImage);
  if (seedId && seedId === picId(finalUrl)) return seedImage;
  return finalLoaded ? finalUrl : seedImage;
}
